import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Divider,
} from '@chakra-ui/react';
import * as React from 'react';

import { Sentinel } from '@/components/Sentinel';

interface Organization {
  _id: string;
  name: string;
  membershipType?: string;
  numberOfSeats?: number | null;
}

interface RequestAccessListModalProps {
  isOpen: boolean;
  onBack: () => void;
  onClose?: () => void;
  onSelectOrganization: (organizationId: string) => void;
  organizations?: Organization[];
  isLoading?: boolean;
  error?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  showAlreadyMemberNote?: boolean;
}

export const RequestAccessListModal: React.FC<RequestAccessListModalProps> = ({
  isOpen,
  onBack,
  onClose,
  onSelectOrganization,
  organizations = [],
  isLoading = false,
  error,
  hasMore,
  onLoadMore,
  loadingMore,
  showAlreadyMemberNote,
}) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose ?? onBack} isCentered size="lg" scrollBehavior="inside" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="640px" maxH="640px" borderRadius="2xl">
        <ModalCloseButton />
        <ModalHeader pt="6">
          <Text fontSize="lg" fontWeight="semibold">
            Request access to a company membership
          </Text>
          <Text fontSize="sm" color="gray.600" fontWeight="normal" mt="2">
            Only memberships that allow employees to request access online are shown.
          </Text>
        </ModalHeader>
        <Divider />
        <ModalBody pb="6">
          {isLoading ? (
            <VStack spacing="4" justify="center" py="8">
              <Spinner colorScheme="blue" />
              <Text color="gray.600">Loading organizations...</Text>
            </VStack>
          ) : error ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          ) : organizations.length === 0 ? (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              No organizations available for employee enrollment.
            </Alert>
          ) : (
            <Stack spacing="3" mt="4">
              {organizations.map((org) => (
                <HStack
                  key={org._id}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  p="4"
                  bg="white"
                  justify="space-between"
                  align="flex-start"
                >
                  <Box flex="1">
                    {(
                      <Box
                        display="inline-block"
                        bg="orange.100"
                        color="orange.800"
                        px="2"
                        fontSize="xs"
                        fontWeight="semibold"
                        borderRadius="md"
                      >
                        Organization approval
                      </Box>
                    )}
                    <Text fontWeight="semibold" fontSize="md" >
                      {org.name}
                    </Text>
                    {(org.membershipType || org.numberOfSeats !== undefined) && (
                      <Text fontSize="sm" color="gray.600">
                        {org.membershipType}
                        {org.membershipType && org.numberOfSeats !== undefined && '  ·  '}
                        {org.numberOfSeats === null || org.numberOfSeats === 0
                          ? 'Unlimited seats'
                          : org.numberOfSeats
                          ? `Up to ${org.numberOfSeats} seats`
                          : ''}
                      </Text>
                    )}
                  </Box>
                  <Button
                    colorScheme="blue"
                    onClick={() => onSelectOrganization(org._id)}
                    size="sm"
                    flexShrink={0}
                    whiteSpace="nowrap"
                    py='5'
                  >
                    Request access
                  </Button>
                </HStack>
              ))}
              
              {hasMore && (
                <Box w="full" py="4">
                  {loadingMore ? (
                    <VStack justify="center">
                      <Spinner colorScheme="blue" size="sm" />
                    </VStack>
                  ) : (
                    <Sentinel
                      onIntersection={() => { if (onLoadMore) onLoadMore(); }}
                      rootMargin="100px 0px"
                    />
                  )}
                </Box>
              )}
            </Stack>
          )}

          { showAlreadyMemberNote && organizations.length > 0 && (
                <Alert status="info" borderRadius="md" mt="2">
                  <AlertIcon />
                  You are already a member of the above organization. If you want its benefits, please request access for it.
                </Alert> 
          )}

        </ModalBody>
        <ModalFooter justifyContent="flex-start" borderTopWidth="1px" borderTopColor="gray.200">
          <HStack spacing="3">
            <Button variant="outline" onClick={onBack} colorScheme="blue">
              Back
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
